import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import { useIntl } from "react-intl";
import { useToasts } from "@magento/peregrine";
import { useCheckoutContext } from "@magento/peregrine/lib/context/checkout";
import { useHistory, useLocation } from "@magento/venia-ui/lib/drivers";
import { useCartContext } from "@overrides_venia/peregrine/context/cart";
import { useCatalogContext } from "@magento/peregrine/lib/context/catalog";
import Modal from "@Components/Modal";
import Icon from "@Components/Icon";
import { ADD_PRODUCT } from "@overrides_venia/venia-ui/RootComponents/Product/product.gql";
import { cdnImageUrl } from "@Src_main/utils/generalUtils";
import { useLocale } from "@Src_main/localeProvider";
import { useUserContext } from "@overrides_venia/peregrine/context/user";
import { env } from "build/env";
import UIkit from "uikit";
import { pickFirstMatchingCampaign } from "./promoModalRules";
import { promoModalCampaigns } from "./promoModalConfig";
import defaultClasses from "./promoModalHost.module.scss";
import { mergeClasses } from "@magento/venia-ui/lib/classify";
var storageKey = (campaignId, suffix) => "miscusi_promo_modal_".concat(campaignId, "_").concat(suffix);
var openPromoOffcanvas = offcanvasId => {
  var elementId = (offcanvasId || "#offcanvas-login-dx").replace(/^#/, "");
  var element = document.getElementById(elementId);
  if (!element) {
    return null;
  }
  try {
    // Keep login panel above the promo modal (UIkit modal default z-index is higher).
    element.style.zIndex = "1020";
    UIkit.offcanvas(element).show();
    return element;
  } catch (error) {
    console.error(error);
    return null;
  }
};
var getOptimizedBackgroundUrl = background => {
  if (!background || background.type !== "image") {
    return "";
  }
  var src = cdnImageUrl(background.src);
  if (background.optimize === false) {
    return src;
  }
  var width = background.width || 560;
  var height = background.height || 800;
  var quality = background.quality || 82;
  var fit = background.fit || "cover";
  var options = "width=".concat(width, ",height=").concat(height, ",fit=").concat(fit, ",quality=").concat(quality, ",format=auto");
  try {
    var url = new URL(src);
    return "".concat(url.origin, "/cdn-cgi/image/").concat(options).concat(url.pathname).concat(url.search);
  } catch (_unused) {
    return src;
  }
};
var PromoModalHost = props => {
  var {
    classes: propClasses
  } = props;
  var classes = mergeClasses(defaultClasses, propClasses);
  var {
    formatMessage
  } = useIntl();
  var {
    language
  } = useLocale();
  var location = useLocation();
  var history = useHistory();
  var [, {
    addToast
  }] = useToasts();
  var [{
    isSignedIn
  }] = useUserContext();
  var [{
    tableData
  }] = useCheckoutContext();
  var [{
    cartId
  }, {
    updateCartData,
    setIsLoadingCart
  }] = useCartContext();
  var [{
    filtered_sku: filteredSku
  }] = useCatalogContext();
  var [addProductToCart] = useMutation(ADD_PRODUCT);
  var [activeCampaign, setActiveCampaign] = useState(null);
  var [isOpen, setIsOpen] = useState(false);
  var [ctaBusy, setCtaBusy] = useState(false);
  var storeLsId = tableData && tableData.store && tableData.store.ls_store_id;
  var storeCode = tableData && (tableData.store && tableData.store.cps || tableData.cps);
  var storeViewOrder = tableData && tableData.store && tableData.store.storeViewOrder;
  var siteStoreViewCode = env.store_view_code;
  var matchContext = useMemo(() => ({
    now: new Date(),
    pathname: location.pathname,
    language,
    availableSkus: filteredSku,
    storeCode,
    storeLsId,
    storeViewOrder,
    siteStoreViewCode,
    isSignedIn
  }), [filteredSku, isSignedIn, language, location.pathname, siteStoreViewCode, storeCode, storeLsId, storeViewOrder]);
  var isDismissed = useCallback(campaign => {
    if (!campaign || campaign.dismissPersistence === "none") {
      return false;
    }
    var key = campaign.dismissPersistence === "forever" ? storageKey(campaign.id, "closed") : storageKey(campaign.id, "session_closed");
    try {
      if (campaign.dismissPersistence === "forever") {
        return window.localStorage.getItem(key) === "1";
      }
      return window.sessionStorage.getItem(key) === "1";
    } catch (_unused2) {
      return false;
    }
  }, []);
  var markDismissed = useCallback(campaign => {
    if (!campaign || campaign.dismissPersistence === "none") {
      return;
    }
    var key = campaign.dismissPersistence === "forever" ? storageKey(campaign.id, "closed") : storageKey(campaign.id, "session_closed");
    try {
      if (campaign.dismissPersistence === "forever") {
        window.localStorage.setItem(key, "1");
      } else {
        window.sessionStorage.setItem(key, "1");
      }
    } catch (_unused3) {
      // ignore quota / private mode
    }
  }, []);
  useEffect(() => {
    setIsOpen(false);
    setActiveCampaign(null);
    var chosen = pickFirstMatchingCampaign(promoModalCampaigns, matchContext);
    if (!chosen || isDismissed(chosen)) {
      return undefined;
    }
    var delay = typeof chosen.openDelayMs === "number" ? chosen.openDelayMs : 0;
    var t = window.setTimeout(() => {
      setActiveCampaign(chosen);
      setIsOpen(true);
    }, delay);
    return () => {
      window.clearTimeout(t);
    };
  }, [matchContext, isDismissed]);
  var handleClose = useCallback(() => {
    if (activeCampaign) {
      markDismissed(activeCampaign);
    }
    setIsOpen(false);
  }, [activeCampaign, markDismissed]);
  var handlePrimary = useCallback( /*#__PURE__*/_asyncToGenerator(function* () {
    if (!activeCampaign) {
      return;
    }
    var cta = activeCampaign.primaryCta;
    if (cta.action === "dismiss") {
      markDismissed(activeCampaign);
      setIsOpen(false);
      return;
    }
    if (cta.action === "navigate" && cta.href) {
      markDismissed(activeCampaign);
      setIsOpen(false);
      history.push(cta.href);
      return;
    }
    if (cta.action === "open_offcanvas") {
      var offcanvasId = cta.offcanvasId || "#offcanvas-login-dx";
      markDismissed(activeCampaign);
      var offcanvasEl = openPromoOffcanvas(offcanvasId);
      if (!offcanvasEl) {
        setIsOpen(false);
        history.push(formatMessage({
          id: "url.key.login"
        }));
        return;
      }

      // Let the offcanvas slide over the promo, then close the promo underneath.
      var promoClosed = false;
      var silentlyClosePromo = () => {
        if (promoClosed) {
          return;
        }
        promoClosed = true;
        UIkit.util.off(offcanvasEl, "shown", silentlyClosePromo);
        setIsOpen(false);

        // If closing the modal cancelled the panel, bring it back once.
        window.setTimeout(() => {
          if (!offcanvasEl.classList.contains("uk-open")) {
            openPromoOffcanvas(offcanvasId);
          }
        }, 50);
      };
      UIkit.util.on(offcanvasEl, "shown", silentlyClosePromo);
      // Fallback if `shown` does not fire for any reason.
      window.setTimeout(silentlyClosePromo, 400);
      return;
    }
    if (cta.action === "add_to_cart") {
      if (!cta.sku) {
        return;
      }
      if (!cartId) {
        addToast({
          type: "error",
          message: formatMessage({
            id: "promoModal.errors.missingCart"
          }),
          timeout: 4000,
          icon: /*#__PURE__*/React.createElement(Icon, {
            name: "icon-error"
          })
        });
        return;
      }
      setCtaBusy(true);
      setIsLoadingCart();
      try {
        var {
          data,
          errors
        } = yield addProductToCart({
          fetchPolicy: "no-cache",
          variables: {
            cartId,
            cartItems: [{
              sku: cta.sku,
              quantity: cta.quantity || 1,
              selected_options: []
            }]
          }
        });
        if (errors && errors.length) {
          throw new Error(errors.map(e => e.message).join("; "));
        }
        if (data && data.addProductsToCart && data.addProductsToCart.cart) {
          updateCartData(data.addProductsToCart.cart);
        }
        markDismissed(activeCampaign);
        setIsOpen(false);
      } catch (e) {
        addToast({
          type: "error",
          message: formatMessage({
            id: "promoModal.errors.addToCart"
          }),
          timeout: 5000,
          icon: /*#__PURE__*/React.createElement(Icon, {
            name: "icon-error"
          })
        });
      } finally {
        setCtaBusy(false);
      }
    }
  }), [activeCampaign, addProductToCart, addToast, cartId, formatMessage, history, markDismissed, setIsLoadingCart, updateCartData]);
  if (!activeCampaign) {
    return null;
  }
  var {
    template,
    background,
    titleMessageId,
    bodyMessageId,
    primaryCta,
    dismissMessageId,
    showDismiss = true,
    showPrimaryCta = true,
    ctaOnly = false
  } = activeCampaign;
  var dismissId = dismissMessageId || "promoModal.demo.dismiss";
  var bgStyle = background.type === "color" ? {
    backgroundColor: background.value
  } : undefined;
  var backgroundPosition = background.type === "image" ? background.position || "center" : undefined;
  var rootClass = template === "compact" ? classes.rootCompact : classes.rootHero;
  var textAlignment = activeCampaign.textAlignment || undefined;
  var contentCardStyle = _objectSpread(_objectSpread({}, activeCampaign.contentBackgroundColor ? {
    background: activeCampaign.contentBackgroundColor
  } : {}), textAlignment ? {
    textAlign: textAlignment
  } : {});
  var showTitle = Boolean(titleMessageId);
  var showBody = Boolean(bodyMessageId);
  var contentCardClass = ctaOnly ? null : showTitle || showBody ? classes.contentCard : "".concat(classes.contentCard, " ").concat(classes.contentCardActionsOnly);
  var scrimClass = ctaOnly ? "".concat(classes.scrim, " ").concat(classes.scrimCtaOnly) : classes.scrim;
  var primaryBtnClass = ctaOnly ? "".concat(classes.primaryBtn, " ").concat(classes.primaryBtnProminent) : classes.primaryBtn;
  var heroAspectRatio = template === "hero" && background.type === "image" && background.intrinsicWidth && background.intrinsicHeight ? "".concat(background.intrinsicWidth, " / ").concat(background.intrinsicHeight) : null;
  var modalDialogStyle = heroAspectRatio ? {
    "--promo-modal-aspect-ratio": heroAspectRatio
  } : undefined;
  return /*#__PURE__*/React.createElement(Modal, {
    id: "promo-modal-".concat(activeCampaign.id),
    isOpen: isOpen,
    onClose: handleClose,
    modalDialogClass: "uk-border-rounded uk-overflow-hidden ".concat(classes.dialog, " ").concat(template === "hero" ? classes.dialogHero : ""),
    modalDialogStyle: modalDialogStyle,
    classNameExtra: classes.modalRoot,
    customZIndex: 990,
    closeButton: false,
    centeredModal: template !== "hero"
  }, /*#__PURE__*/React.createElement("div", {
    className: "".concat(classes.inner, " ").concat(rootClass),
    style: bgStyle
  }, background.type === "image" ? /*#__PURE__*/React.createElement("img", {
    className: classes.backgroundImage,
    src: getOptimizedBackgroundUrl(background),
    alt: background.altMessageId ? formatMessage({
      id: background.altMessageId
    }) : "",
    style: backgroundPosition ? {
      objectPosition: backgroundPosition
    } : undefined
  }) : null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: classes.closeButton,
    onClick: handleClose,
    "aria-label": formatMessage({
      id: dismissId
    }),
    disabled: ctaBusy
  }, /*#__PURE__*/React.createElement(Icon, {
    name: "icon-close",
    color: "white",
    header: true
  })), /*#__PURE__*/React.createElement("div", {
    className: scrimClass
  }, ctaOnly ? /*#__PURE__*/React.createElement("div", {
    className: classes.ctaDock
  }, /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, showPrimaryCta ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: primaryBtnClass,
    style: {
      backgroundColor: primaryCta.backgroundColor || "#fff",
      color: primaryCta.textColor || "#222",
      textAlign: textAlignment
    },
    disabled: ctaBusy,
    "aria-busy": ctaBusy,
    onClick: handlePrimary
  }, ctaBusy ? /*#__PURE__*/React.createElement("span", {
    className: classes.buttonSpinner,
    "uk-spinner": "ratio: 0.65",
    "aria-hidden": false
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: classes.primaryBtnLabel
  }, formatMessage({
    id: primaryCta.labelMessageId
  }))) : null, showDismiss ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: classes.secondaryBtn,
    onClick: handleClose,
    disabled: ctaBusy
  }, formatMessage({
    id: dismissId
  })) : null)) : /*#__PURE__*/React.createElement("div", {
    className: contentCardClass,
    style: contentCardStyle
  }, showTitle ? /*#__PURE__*/React.createElement("h2", {
    className: classes.title
  }, formatMessage({
    id: titleMessageId
  })) : null, showBody ? /*#__PURE__*/React.createElement("p", {
    className: classes.body
  }, formatMessage({
    id: bodyMessageId
  })) : null, /*#__PURE__*/React.createElement("div", {
    className: classes.actions
  }, showPrimaryCta ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: primaryBtnClass,
    style: {
      backgroundColor: primaryCta.backgroundColor || "#fff",
      color: primaryCta.textColor || "#222",
      textAlign: textAlignment
    },
    disabled: ctaBusy,
    "aria-busy": ctaBusy,
    onClick: handlePrimary
  }, ctaBusy ? /*#__PURE__*/React.createElement("span", {
    className: classes.buttonSpinner,
    "uk-spinner": "ratio: 0.65",
    "aria-hidden": false
  }) : null, /*#__PURE__*/React.createElement("span", {
    className: classes.primaryBtnLabel
  }, formatMessage({
    id: primaryCta.labelMessageId
  }))) : null, showDismiss ? /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: classes.secondaryBtn,
    onClick: handleClose,
    disabled: ctaBusy
  }, formatMessage({
    id: dismissId
  })) : null)))));
};
export default PromoModalHost;